import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NO_CATEGORY = "__none__";

const empty = {
    name: "",
    host: "",
    port: 80,
    scheme: "http",
    description: "",
    health_check_interval: 60,
    icon_url: "",
    tags: "",
    category_id: NO_CATEGORY,
    failure_threshold: 2,
};

export default function AppDialog({
    open,
    onOpenChange,
    initial,
    onSaved,
    categories = [],
    defaultCategoryId = "",
}) {
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const editing = Boolean(initial?.id);

    useEffect(() => {
        if (open) {
            if (initial?.id) {
                setForm({
                    name: initial.name || "",
                    host: initial.host || "",
                    port: initial.port || 80,
                    scheme: initial.scheme || "http",
                    description: initial.description || "",
                    health_check_interval: initial.health_check_interval || 60,
                    icon_url: initial.icon_url || "",
                    tags: (initial.tags || []).join(", "),
                    category_id: initial.category_id || NO_CATEGORY,
                    failure_threshold: initial.failure_threshold || 2,
                });
            } else {
                // Pre-select the category currently being browsed, if any.
                setForm({ ...empty, category_id: defaultCategoryId || NO_CATEGORY });
            }
        }
    }, [open, initial, defaultCategoryId]);

    const submit = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                host: form.host.trim(),
                port: parseInt(form.port, 10),
                scheme: form.scheme,
                description: form.description?.trim() || null,
                health_check_interval: parseInt(form.health_check_interval, 10) || 60,
                icon_url: form.icon_url?.trim() || null,
                tags: form.tags
                    ? form.tags.split(",").map((s) => s.trim()).filter(Boolean)
                    : [],
                // "" tells the API to clear the assignment.
                category_id: form.category_id === NO_CATEGORY ? "" : form.category_id,
                failure_threshold: parseInt(form.failure_threshold, 10) || 2,
            };
            if (!body.name || !body.host) {
                toast.error("Name and host are required");
                setSaving(false);
                return;
            }
            if (editing) {
                await api.put(`/apps/${initial.id}`, body);
                toast.success("App updated");
            } else {
                await api.post("/apps", body);
                toast.success("App added");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]" data-testid="app-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display tracking-tight">
                        {editing ? "Edit App" : "Add new app"}
                    </DialogTitle>
                    <DialogDescription>
                        Register a service to monitor and access from your dashboard.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-2">
                    <div className="grid gap-1.5">
                        <Label htmlFor="app-name">Name</Label>
                        <Input
                            id="app-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Plex Media Server"
                            data-testid="input-app-name"
                        />
                    </div>
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-3 grid gap-1.5">
                            <Label htmlFor="app-scheme">Scheme</Label>
                            <Select value={form.scheme} onValueChange={(v) => setForm({ ...form, scheme: v })}>
                                <SelectTrigger id="app-scheme" data-testid="select-app-scheme">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="http">http</SelectItem>
                                    <SelectItem value="https">https</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-6 grid gap-1.5">
                            <Label htmlFor="app-host">Host / IP</Label>
                            <Input
                                id="app-host"
                                value={form.host}
                                onChange={(e) => setForm({ ...form, host: e.target.value })}
                                placeholder="192.168.1.10"
                                data-testid="input-app-host"
                            />
                        </div>
                        <div className="col-span-3 grid gap-1.5">
                            <Label htmlFor="app-port">Port</Label>
                            <Input
                                id="app-port"
                                type="number"
                                value={form.port}
                                onChange={(e) => setForm({ ...form, port: e.target.value })}
                                data-testid="input-app-port"
                            />
                        </div>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="app-desc">Description</Label>
                        <Textarea
                            id="app-desc"
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Optional summary shown on hover."
                            data-testid="input-app-description"
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="app-category">Category</Label>
                        <Select
                            value={form.category_id}
                            onValueChange={(v) => setForm({ ...form, category_id: v })}
                        >
                            <SelectTrigger id="app-category" data-testid="select-app-category">
                                <SelectValue placeholder="Uncategorized" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NO_CATEGORY}>
                                    <span className="text-muted-foreground">Uncategorized</span>
                                </SelectItem>
                                {categories.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        <span className="mr-2">{c.icon}</span>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="app-interval">Health check (seconds)</Label>
                            <Input
                                id="app-interval"
                                type="number"
                                min={10}
                                value={form.health_check_interval}
                                onChange={(e) =>
                                    setForm({ ...form, health_check_interval: e.target.value })
                                }
                                data-testid="input-app-interval"
                            />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="app-threshold">Failures before offline</Label>
                            <Input
                                id="app-threshold"
                                type="number"
                                min={1}
                                value={form.failure_threshold}
                                onChange={(e) =>
                                    setForm({ ...form, failure_threshold: e.target.value })
                                }
                                data-testid="input-app-threshold"
                            />
                        </div>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="app-tags">Tags (comma separated)</Label>
                        <Input
                            id="app-tags"
                            value={form.tags}
                            onChange={(e) => setForm({ ...form, tags: e.target.value })}
                            placeholder="media, internal"
                            data-testid="input-app-tags"
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="app-icon">Icon URL (optional, auto-fetched if empty)</Label>
                        <Input
                            id="app-icon"
                            value={form.icon_url}
                            onChange={(e) => setForm({ ...form, icon_url: e.target.value })}
                            placeholder="https://..."
                            data-testid="input-app-icon"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-app-dialog">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={saving} data-testid="save-app-dialog">
                        {saving ? "Saving..." : editing ? "Save changes" : "Add app"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
